import {
  Button,
  ContentReferences,
  DeleteConfirmButton,
  DevDetailsButton,
  EllipsisVerticalIcon,
  Flex,
  HelpHover,
  IconButton,
  MarkdownEditor,
  Panel,
  Popover,
  Text,
  TextField,
} from 'components'
import {
  AfterRegistrationConsentEditor,
  FollowUpRegistrationConsentEditor,
  RegistrationConsentProvider,
  useRegistrationConsent,
} from '../components/consentContent'

export default function RegistrationConsentInfoPage() {
  return (
    <RegistrationConsentProvider>
      <RegistrationConsentInfoContent />
    </RegistrationConsentProvider>
  )
}

function RegistrationConsentInfoContent() {
  const { consent, deleteConsent, isDirty, resetConsent, saveConsent, updateConsent } =
    useRegistrationConsent()

  if (!consent) return null

  const { title, id, during, before } = consent

  return (
    <Flex
      direction="column"
      flex="auto"
      p={{ base: 5, md: 0 }}
      pb={{ base: undefined, md: 4 }}
      pr={{ base: undefined, md: 5 }}
      overflowY="auto"
      overflowX="hidden"
      mt={2}
      gap={4}
    >
      <Flex alignItems="center" justifyContent="space-between" gap={4}>
        <TextField
          maxWidth="300px"
          value={title}
          onChange={e => updateConsent({ title: e.target.value })}
          placeholder="Registration Consent Title"
        />

        {isDirty && (
          <Flex gap={1}>
            <Button onClick={resetConsent} variant="ghost">
              Reset
            </Button>
            <Button onClick={saveConsent} palette="primary">
              Save
            </Button>
          </Flex>
        )}

        <ContentReferences variant="menu" type="registrationConsent" id={id} />
        <Popover
          trigger={<IconButton icon={<EllipsisVerticalIcon />} aria-label="Delete" />}
          hideClose
          positioning={{ placement: 'bottom-end' }}
        >
          <DeleteConfirmButton onDelete={deleteConsent} />
        </Popover>
        <DevDetailsButton
          items={[{ type: 'db', collection: 'registrationConsents', id, label: 'Consent' }]}
        />
      </Flex>

      <Flex direction="column" gap={6} flex={1}>
        <Panel p={6} gap={6}>
          <Flex direction="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Text textStyle="h5">Before Registration</Text>
              <HelpHover text="Used to block registration (ex. make sure the attendee is old enough)" />
            </Flex>
            <MarkdownEditor
              value={before?.text?.en || ''}
              onChange={value => updateConsent({ before: { text: { en: value } } })}
            />
          </Flex>

          <Flex direction="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Text textStyle="h5">During Registration</Text>
              <HelpHover text="Notice visible during registration that is accepted if they submit their details" />
            </Flex>
            <MarkdownEditor
              value={during?.text?.en || ''}
              onChange={value => updateConsent({ during: { text: { en: value } } })}
            />
          </Flex>

          <Flex direction="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Text textStyle="h5">After Registration</Text>
              <HelpHover text="Notice visible after registration is complete useful for explicit marketing consent" />
            </Flex>
            <AfterRegistrationConsentEditor />
          </Flex>

          <Flex direction="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Text textStyle="h5">Follow Up</Text>
              <HelpHover text="Final follow up consent visible after the session is complete to see if they want someone to reach out to them" />
            </Flex>
            <FollowUpRegistrationConsentEditor />
          </Flex>
        </Panel>
      </Flex>
    </Flex>
  )
}
